import {Component, OnInit, HostListener} from '@angular/core';
import {ApiService, I18nHelperService, QueryStringService} from '@xref/ngx';
import * as Cookies from 'js-cookie';
import moment from 'moment';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import { Location } from '@angular/common';

import {environment} from '../environments/environment';
import {MaintenanceService} from './services/maintenance/maintenance.service';
import {StylingCheckService} from './services/styling-check/styling-check.service';
import {delay} from 'rxjs/operators';
import Constants from './shared/constants';
import {IdService} from "./services/id/id.service";
import {PermissionsService} from "./services/permissions/permissions.service";

enum SignupState {
    NONE = 'none',
    INITIAL = 'initial',                                        // /signup, /signup/expired
    VERIFY_EMAIL = 'verify_email',                              // /signup/verify
    JOIN_TEAM = 'join_team',                                    // /signup/join-team
    USER_DETAILS = 'user_details',                              // /user-details
    EXISTING_USER_ORG_CREATION = 'existing_user_org_creation'   // /user-verify, /user-logged-in, /org-creation'
}

@Component({
    selector: 'xlog-app',
    templateUrl: './app.component.pug',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
    readonly MODAL_DESIGN: SignupState[] = [
        SignupState.INITIAL,
        SignupState.VERIFY_EMAIL,
        SignupState.USER_DETAILS
    ];
    readonly FULL_PAGE_DESIGN: SignupState[] = [
        SignupState.EXISTING_USER_ORG_CREATION,
        SignupState.JOIN_TEAM
    ];
    readonly SPLIT_PAGE_DESIGN: SignupState[] = [SignupState.NONE];
    classic: any;
    loaded = false;
    contrast = false;
    moment: any;
    currentUrl: string;
    currentState: SignupState = SignupState.NONE;
    signupFlow = false;
    signupVerify = false;
    signupJoinTeam = false;
    signUpUserFlow = false;
    ff_ent_10781_sign_up = false;

    screenWidth: number;
    screenType = 'default';
    previousUrl = '';

    constructor(
        private location: Location,
        private id: IdService,
        private api: ApiService,
        public maintenance: MaintenanceService,
        private stylingCheck: StylingCheckService,
        private i18nHelper: I18nHelperService,
        private qs: QueryStringService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        public featureFlagService: PermissionsService
    ) { }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.screenWidth = window.innerWidth;
        this.updateScreenType();
    }

    ngOnInit() {
        this.id.ent_9105 = this.featureFlagService.isFeatureActivated('ENT-9105-PENETRATION-TEST-2025');
        this.ff_ent_10781_sign_up = this.featureFlagService.isFeatureActivated('ent-10781-signup-improvements');
        this.screenWidth = window.innerWidth;
        this.updateScreenType();

        this.currentUrl = this.location.path();

        if (this.ff_ent_10781_sign_up) {
            this.currentState = this.getSignupState(this.currentUrl);
        }

        if (this.currentUrl === '/signup' || this.currentUrl === '/signup/expired') {
            this.signupFlow = true;
        }
        if (this.currentUrl.startsWith('/signup/verify')) {
            this.signupVerify = true;
        }
        if (this.currentUrl.startsWith('/signup/join-team')) {
            this.signupJoinTeam = true;
        }
        if (this.currentUrl.startsWith('/user-details') || this.currentUrl === '/user-verify' || this.currentUrl === '/user-logged-in' || this.currentUrl === '/org-creation') {
            this.signUpUserFlow = true;
        }

        this.api.config.errorResponseHandler = this.apiErrorResponseHandler;

        this.i18nInit();

        this.moment = moment;

        this.showContrastMode();

        this.loaded = true;
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.classic = true;
                if (this.activatedRoute.firstChild.snapshot.data.omni) {
                    this.classic = false;
                }

                if (this.ff_ent_10781_sign_up) {
                    this.currentState = this.getSignupState(this.location.path());
                }
                // once signup improvements project is complete, these status variables can be removed.
                this.signupFlow = this.activatedRoute.firstChild.snapshot.data.url === 'signup';
                this.signupVerify = this.activatedRoute?.['_routerState']?.snapshot?.url?.startsWith("/signup/verify");
                this.signupJoinTeam = this.activatedRoute?.['_routerState']?.snapshot?.url?.startsWith("/signup/join-team");

                const currentUrl = window.location.href;
                const pageTitle = document.title;

                (window as any).dataLayer = (window as any).dataLayer || [];
                (window as any).dataLayer.push({
                    event: 'virtualPageview',
                    pageUrl: currentUrl,
                    pageTitle: pageTitle,
                    previousUrl: this.previousUrl
                });
                this.previousUrl = currentUrl;
            }
        });
        this.stylingCheck.getClassic().pipe(delay(0)).subscribe(data => {
            this.classic = data;
        });
    }

    getSignupState(url: string): SignupState {
        if (url === '/signup' || url.startsWith('/signup?') || url.startsWith('/signup/expired')) {
            return SignupState.INITIAL;
        }
        if (url.startsWith('/signup/verify')) {
            return SignupState.VERIFY_EMAIL;
        }
        if (url.startsWith('/signup/join-team')) {
            return SignupState.JOIN_TEAM;
        }
        if (url.startsWith('/user-details')) {
            return SignupState.USER_DETAILS;
        }
        if (url.startsWith('/user-verify') || url.startsWith('/user-logged-in') || url.startsWith('/org-creation')) {
            return SignupState.EXISTING_USER_ORG_CREATION;
        }
        return SignupState.NONE;
    }

    updateScreenType(){
        if (this.screenWidth <= 375){
            this.screenType = 'mobile';
        } else if (this.screenWidth < 1280 && this.screenWidth > 375){
            this.screenType = 'tablet';
        } else {
            this.screenType = 'default';
        }
    }

    i18nInit() {
    // Workaround: setting LOCALE config here as can't call a function to do it in app.module anymore, with the uprgade
        this.i18nHelper.config['LOCALE'] = (Cookies.get(Constants.LOCALE_TOKEN) || Constants.LOCALE);
        this.i18nHelper.init();

        const parsedLocale = this.qs.getParam('l');

        if (parsedLocale && !this.i18nHelper.getStoredLocale()) {
            this.i18nHelper.changeLocale(parsedLocale.toLocaleLowerCase());
        }
    }

    showContrastMode() {
        this.contrast = Cookies.get(Constants.CONTRAST) === 'true';
    }

    setContrastMode() {
        Cookies.set(Constants.CONTRAST, this.contrast, { domain: environment.DOMAIN });
    }

    apiErrorResponseHandler = (err: any) => {
        switch (err.status) {
            case 401:
                this.id.logout();
                break;
            case 429:
            case 503:
                // Remove token and set maintenance mode
                this.id.removeToken();
                this.maintenance.setMaintenance();
                break;
        }
    };

    getPrivacyUrl() {
        const locale = Cookies.get(Constants.LOCALE_TOKEN);
        let privacyUrl = 'https://www.xref.com/privacy-policy?src=xref-platform&ltyp=omni&cn=sign-up-app';

        switch (locale) {
            case 'sv-se':
                privacyUrl = 'https://xref.com/sv/integritetspolicy/';
                break;
            case 'nb-no':
                privacyUrl = 'https://www.xref.com/nb/no/personvernerkl%C3%A6ring/';
                break;
        }

        return privacyUrl;
    }

    onCloseClicked() {
        window.open("https://www.xref.com/", "_self");
    }

    getCloseButtonClass() {
        if (this.screenWidth < 992) {
            return 'icon-button-black';
        } else {
            return 'icon-button-white';
        }
    }

    getCloseButtonSize() {
        if (this.screenWidth < 576) {
            return 'xsml';
        } else if (this.screenWidth < 992) {
            return 'sml';
        } else {
            return null;
        }
    }
}
